import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from "@angular/core";
import { AuthenticationService, CommentModel, PipeModule } from "@alfresco/adf-core";
import {MatListModule} from "@angular/material/list";
import {TranslateModule} from "@ngx-translate/core";
import {NgForOf, NgIf} from "@angular/common";
import {MatLineModule} from "@angular/material/core";
import {AlfrescoApi, CommentsApi} from "@alfresco/js-api";
import {from} from "rxjs";
import {MatFormFieldModule} from "@angular/material/form-field";
import {FormsModule} from "@angular/forms";
import {MatInputModule} from "@angular/material/input";
import {MatButtonModule} from "@angular/material/button";
import { AlfrescoApiService, NodeCommentsService } from "@alfresco/adf-content-services";
import {MatCardModule} from "@angular/material/card";
import {MatIconModule} from "@angular/material/icon";
import {CommentSyncService} from "../../services/comment-sync.service";

@Component({
  selector: 'rc-comments',
  templateUrl: './rc-comments.html',
  styleUrls: ['./rc-comments.scss'],
  standalone: true,
  imports: [
    MatListModule,
    TranslateModule,
    PipeModule,
    NgForOf,
    NgIf,
    MatLineModule,
    MatFormFieldModule,
    FormsModule,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
  ]
})
export class RcComments implements OnChanges, OnInit {
  /** The numeric ID of the task. */
  @Input()
  id: string;
  @Input() nodeId: string;
  @Input() readOnly: boolean;
  @Output()
  error = new EventEmitter<any>();
  comments: CommentModel[] = [];
  message: string;
  beingAdded: boolean = false;
  commentInEdit: CommentModel;
  messageEdit: any;
  currentUserId: string | number;
  protected readonly alfrescoApi: AlfrescoApi;

  constructor(
    private apiService: AlfrescoApiService,
    private commentsService: NodeCommentsService,
    private authService: AuthenticationService,
    private commentSyncService: CommentSyncService
  ) {
  }

  private _commentsApi: CommentsApi;

  get commentsApi(): CommentsApi {
    this._commentsApi = this._commentsApi ?? new CommentsApi(this.apiService.getInstance());
    return this._commentsApi;
  }

  ngOnInit() {
    this.currentUserId = this.getCurrentUser();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.id = null;
    this.id = changes['nodeId'] ? changes['nodeId'].currentValue : null;
    if (this.id) {
      this.loadComments();
    } else {
      this.resetComments();
    }
  }

  getCurrentUser() {
    return this.authService.getUsername();
  }

  loadComments() {
    this.resetComments();

    if (!this.hasId()) {
      return;
    }

    this.commentsService.get(this.id).subscribe(
      (comments: CommentModel[]) => {
        if (!this.isArrayInstance(comments)) {
          return;
        }

        comments = this.sortedComments(comments);
        this.comments.push(...comments);
      },
      (err) => {
        this.error.emit(err);
      }
    );
  }

  selectComment(comment: CommentModel) {
    console.log(comment);
  }

  getUserImage(createdBy: any) {
    console.log(createdBy);
  }

  editComment(comment: CommentModel) {
    from(this.commentsApi.updateComment(this.nodeId, comment.id.toString(), {content: this.messageEdit})).subscribe((res) => {
      comment.message = res.entry.content;
      this.commentInEdit = null;
      this.messageEdit = null;
    });
  }

  deleteComment(comment: CommentModel) {
    from(this.commentsApi.deleteComment(this.nodeId, comment.id.toString())).subscribe(() => {
      this.comments = this.comments.filter((c) => c.id !== comment.id);
      this.commentSyncService.updateCommentCount(this.nodeId, this.comments.length);
    });
  }

  addComment() {
    if (!this.canAddComment()) {
      return;
    }

    this.beingAdded = true;

    this.commentsService.add(this.id, this.message)
      .subscribe(
        (res: CommentModel) => {
          this.addToComments(res);
          this.resetMessage();
          this.commentSyncService.updateCommentCount(this.nodeId.toString(), this.comments.length);
        },
        (err) => {
          this.error.emit(err);
        },
        () => {
          this.beingAdded = false;
        }
      );
  }

  clearMessage(event: Event): void {
    event.stopPropagation();
    this.resetMessage();
  }

  openEdit(comment: CommentModel) {
    if (this.commentInEdit && this.commentInEdit.id === comment.id) {
      this.commentInEdit = null;
      this.messageEdit = null;
      return;
    }
    this.commentInEdit = comment;
    this.messageEdit = comment.message;
  }

  private addToComments(comment: CommentModel): void {
    this.comments.unshift(comment);
  }

  private resetMessage(): void {
    this.message = '';
  }

  private canAddComment(): boolean {
    return this.hasId() && this.message && this.message.trim() && !this.beingAdded;
  }

  private hasId(): boolean {
    return !!this.id;
  }

  private isArrayInstance(entity: any): boolean {
    return entity && entity instanceof Array;
  }

  private sortedComments(comments: CommentModel[]): CommentModel[] {
    return comments.sort((comment1: CommentModel, comment2: CommentModel) => {
      const date1 = new Date(comment1.created);
      const date2 = new Date(comment2.created);

      return date1 > date2 ? -1 : date1 < date2 ? 1 : 0;
    });
  }

  private resetComments(): void {
    this.comments = [];
  }
}
